import React from 'react'
import {
  cloneDeep,
  findIndex,
  isEmpty,
  isEqual,
  map,
  uniq,
  uniqBy,
} from 'lodash'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { parse } from 'query-string'
import { IconX } from '@tabler/icons-react'

import Button from 'components/ui/button'
import Tooltip from 'components/tooltip/Tooltip'
import { trackEvent, EVENTS } from 'utils/tracking/events-tracker'

import Modal, {
  ModalContent,
  ModalHeader,
  RowContent,
} from '../../components/modal/Modal'
import Stepper from '../../components/stepper'
import {
  sanitizeDomain,
  getDomain,
  generateKey,
  mapLocLang,
  sendMessage,
} from '../../utils/functions'
import {
  getAvailableKeywords,
  createDashboardCompetitorAnalysisReport,
  getCompetitorAnalysisReport,
  validateSite,
  getSiteData,
  queryDomainOverview,
  startSiteAudit,
} from '../../utils/api'
import { mediaQueries, palette } from '../../styles/variables'
import {
  getMaxValuesForUser,
  getMaxValuesForProject,
  isPaidUser,
  shouldBlockFeature,
  DASHBOARD,
  isFreeTrialUsed,
  BlockFeatureAction,
  isLimitDisabled,
} from '../../utils/freeVersionBlock'
import { translate, replaceWithLanguageRoutes } from '../../utils/translation'
import history from '../../history'
import ConfirmationModal from '../../components/modal/ConfirmationModal'
import {
  KISSMETRICS_TRACK_EVENTS,
  kissmetricsRecordingEvent,
  PROPERTY_NAMES,
  PROPERTY_VALUES,
} from '../../utils/kissmetrics'
import {
  getLocations,
  getLocationInfo,
  filterCompetitorsLocations,
} from '../../utils/locations'
import {
  selectCurrentUser,
  selectNewLimitsChangingPlans,
} from '../../state/selectors'
import { selectCachedLocations } from '../../state/selectors/locationManager'
import { addProject } from '../Dashboard/actions'
import { NavLogoImg } from '../NavBar/styled'
import logo from '../../styles/assets/ubersuggest-logo-orange.svg'
import { Heading2A } from '../../components/topography'
import { VALID_STATUS } from '../../components/input/TagInput/CompetitorTagInput'
import { AIWRITER_BASE_URL, REPORT_POLL_DELAY } from '../../utils/consts.mjs'
import {
  getUnloggedUserProject,
  getUnloggedUserProjectCheckout,
  hasUnloggedUserProject,
  hasUnloggedUserProjectCheckout,
  removeUnloggedUserProject,
  removeUnloggedUserProjectCheckout,
  saveRecentProject,
  saveUnloggedUserProject,
  saveUnloggedUserProjectCheckout,
} from '../../utils/storage'
import { isSignedIn, isSubUser } from '../../utils/userAuth'
import EditLocationsModal from '../../components/modal/EditLocationsModal'
import HubSpotService from '../../utils/hubspot'
import { MIXPANEL_PROPS, Mixpanel } from '../../utils/mixpanel'
import SignupModal from '../../components/modal/signupModal'
import { TurnstileProvider } from '../../components/turnstile'
import checkoutModal from '../../components/modal/checkoutModal'
import { getAddonByKey } from '../../utils/payment'
import { allAddonsTypes } from '../../utils/addonTypes'
import { useLeadCaptureStore } from '../../features/lead-capture/store/lead-capture-store'

import SiteInsightPreview from './steps/SiteInsightPreview'
import Step6 from './steps/Step6'
import Step4 from './steps/Step4'
import Step3 from './steps/Step3'
import Step2 from './steps/Step2'
import Step1 from './steps/Step1'

const Content = styled(RowContent)((props) => ({
  display: props.hidden ? 'none' : 'flex',
  alignItems: 'flex-start',
  height: '100%',
  width: '100%',
  '& .add-project-step-wizard': {
    display: 'block',
    justifyContent: 'center',
    height: '100%',
    '& > div': {
      [mediaQueries.mobile]: {
        width: '100%',
      },
    },
    '& > div, & > div > div': {
      height: 'auto',
    },
  },
  [`@media (max-width: 1023px)`]: {
    ...(props.activeStep === 5 && {
      flexDirection: 'column',
      alignItems: 'center',
      height: '100%',
      width: '100%',
      '& .add-project-step-wizard': {
        width: '100%',
        marginBottom: '20px',
      },
      '& > div:first-child': {
        width: '100%',
        maxWidth: '100%',
      },
      '& > div:last-child': {
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
      },
    }),
  },
  [mediaQueries.mobile]: {
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
}))

const ModalHeaderContent = styled.div({
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const CreteProjectText = styled(Heading2A)({
  color: palette.darkGray50,
  flex: 1,
  justifyContent: 'center',
})

const stepsWithGrow = [
  {
    title: 'step1_website',
    description: 'step1_website_description',
  },
  {
    title: 'step2_languages_locations',
    description: 'step2_languages_locations_description',
  },
  {
    title: 'step3_keyword_rank_tracking',
    description: 'step3_keyword_rank_tracking_description',
  },
  {
    title: 'step4_track_competitors',
    description: 'step4_track_competitors_description',
  },
  {
    title: 'step6_lets_grow_your_traffic',
  },
]
const stepsWithoutGrow = [
  {
    title: 'step1_website',
    description: 'step1_website_description',
  },
  {
    title: 'step2_languages_locations',
    description: 'step2_languages_locations_description',
  },
  {
    title: 'step3_keyword_rank_tracking',
    description: 'step3_keyword_rank_tracking_description',
  },
  {
    title: 'step4_track_competitors',
    description: 'step4_track_competitors_description',
  },
  {
    title: 'step6_want_grow_your_traffic',
  },
]

const desktopStyle = {
  width: '100%',
  height: '100%',
  minHeight: '100%',
  overflow: 'unset',
  overflowY: 'visible',
  maxHeight: '100vh',
  borderRadius: 0,
  display: 'flex',
  flexDirection: 'column',
}
const mobileStyle = (fitContent) => ({
  width: '100%',
  height: fitContent ? 'fit-content' : '100%',
  borderRadius: 0,
  display: 'flex',
  flexDirection: 'column',
})
class AddProjectModal extends React.PureComponent {
  constructor(props) {
    super(props)

    const { eventProps, initialState } = this.props
    this.state = this.getInitialState({
      ...initialState,
      eventProps: initialState?.eventProps
        ? initialState?.eventProps
        : { addProjectButtonLocation: 'N/A', ...eventProps },
      tryAnotherPaymentMethodModal: {
        isOpen: false,
      },
      pendingConfirmModal: null,
    })
    this.locations = []
    new Promise((resolve) => {
      const locations = getLocations(true)
      resolve(locations)
    }).then((locations) => {
      this.locations = locations
    })
    this.contentRef = React.createRef()
  }

  getInitialState(initialState = {}) {
    const currentInitialState = initialState
    const { cachedLocations } = this.props
    const isPaid = isPaidUser()
    const { RANKING_UPDATE_FREQUENCY } = getMaxValuesForUser()
    currentInitialState.locations =
      currentInitialState.locations?.map((item) => ({
        ...getLocationInfo(item.loc_id, cachedLocations),
        languageCode: item.lang,
      })) || []

    let state = {
      isStepWizardOpen: true,
      activeStep: 1,
      domain: '',
      previousDomain: '',
      isFreeUser: false,
      title: '',
      keywords: [],
      competitors: [],
      pendingCompetitorData: false,
      locations: [],
      previousLocations: [],
      competitorsLocations: [],
      suggestedCompetitors: [],
      locSearch: '',
      rankTrackingFrequency: RANKING_UPDATE_FREQUENCY,
      mobileRankTracking: !shouldBlockFeature(
        DASHBOARD,
        'actions',
        `mobile_rank_tracking_on`,
      ),
      biweeklyCall: isPaid,
      seoHelp: isPaid,
      growYourTraffic: false,
      leadsData: null,
      seoPerformance: {
        domainOverview: {
          isLoading: false,
        },
        competitors: [],
      },
      siteData: {
        isLoading: false,
      },
      confirmModal: {
        isOpen: false,
      },
      saveModal: {
        isOpen: false,
      },
      editLocationsModal: {
        isOpen: false,
      },
      isProcessingCheckout: false,
      isLoading: false,
      isSignupModalOpen: false,
      isCheckoutModalOpen: false,
      ...currentInitialState,
    }

    if (
      isSignedIn() &&
      (hasUnloggedUserProject() || hasUnloggedUserProjectCheckout())
    ) {
      const project =
        getUnloggedUserProject() || getUnloggedUserProjectCheckout()
      const {
        domain,
        keywords,
        availableKeywords,
        competitors,
        locations,
        previousLocations,
        competitorsLocations,
        locSearch,
        growYourTraffic,
        leadsData,
        activeStep,
        seoPerformance = state.seoPerformance,
        siteData = state.siteData,
        eventProps,
      } = project

      state = {
        ...state,
        activeStep,
        domain,
        locations,
        previousLocations,
        competitorsLocations,
        locSearch,
        availableKeywords,
        suggestedCompetitors: [],
        competitors,
        keywords,
        growYourTraffic,
        leadsData,
        seoPerformance,
        siteData,
        eventProps,
      }

      if (domain && competitorsLocations.length) {
        this.getSuggestedCompetitors(
          domain,
          competitorsLocations[0].loc_id,
          competitorsLocations[0].lang,
        )
      }
    }
    return state
  }

  componentDidMount() {
    window.addEventListener('message', this.receiveMessage, false)
  }

  componentDidUpdate(prevProps) {
    const { isOpen, initialState, user, eventProps } = this.props

    if (!prevProps.isOpen && isOpen) {
      this.setState(
        this.getInitialState({
          invalidDomain: null,
          ...initialState,
        }),
      )
    }
    if (!isEqual(prevProps.user, user)) {
      const { RANKING_UPDATE_FREQUENCY } = getMaxValuesForUser()
      const isPaid = isPaidUser()

      this.setState({
        rankTrackingFrequency: RANKING_UPDATE_FREQUENCY,
        mobileRankTracking: !shouldBlockFeature(
          DASHBOARD,
          'actions',
          `mobile_rank_tracking_on`,
        ),
        biweeklyCall: isPaid,
        seoHelp: isPaid,
      })
    }
    if (!isEqual(prevProps.eventProps, eventProps)) {
      this.setState({
        eventProps: initialState?.eventProps
          ? initialState?.eventProps
          : {
              addProjectButtonLocation: 'N/A',
              ...eventProps,
            },
      })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.receiveMessage, false)
  }

  getMaxValuesForProject() {
    const { userProjects } = this.props
    const { MAX_PROJECTS_PER_USER, MAX_AVAILABLE_ADDON_PROJECTS } =
      getMaxValuesForUser()

    const totalProjectsCount = userProjects ? userProjects.length : 0
    const availableProjectsCount = MAX_PROJECTS_PER_USER - totalProjectsCount

    const newAddonProject =
      MAX_AVAILABLE_ADDON_PROJECTS > 0 &&
      availableProjectsCount === MAX_AVAILABLE_ADDON_PROJECTS

    return getMaxValuesForProject(null, newAddonProject)
  }

  receiveMessage = (event) => {
    if (event.origin !== window.location.origin) return
    const message =
      typeof event.data === 'string' ? event.data : event.data.message
    if (message === 'SAVE_NEW_PROJECT') {
      this.saveProject(true)
    }
    if (
      message === 'SAVE_NEW_PROJECT_WITH_LEADS_DATA' &&
      this.state.eventProps.addProjectButtonLocation !== 'N/A'
    ) {
      this.saveProjectWithLeadsData(this.state.leadsData, true)
    }
    if (message === 'CLOSE_ADD_PROJECT_MODAL') {
      this.props.closeAddProjectModal()
    }
    if (message === 'URL_ALREADY_USED') {
      this.validateStep1()
    }
    if (
      message === 'LEAD_CAPTURE_SUCCESS_CLOSED' &&
      this.state.pendingConfirmModal
    ) {
      this.showConfirmModal(this.state.pendingConfirmModal)
      this.setState({ pendingConfirmModal: null })
    }
  }

  getSuggestedCompetitors = (
    domain,
    locId,
    lang,
    extraCompetitors,
    limit,
    includeSuggested = true,
  ) => {
    this.setState({ pendingCompetitorData: true, suggestedCompetitors: [] })
    createDashboardCompetitorAnalysisReport(
      undefined, // this is for getting suggested list of competitors
      domain,
      locId,
      lang,
      extraCompetitors,
      includeSuggested,
      limit,
    )
      .then((reportRes) => {
        const { reportId } = reportRes.data
        if (reportId) {
          this.queryCompetitorAnalysis(reportId)
        } else {
          throw new Error('Invalid reportId')
        }
      })
      .catch((error) => {
        console.error('error', error)
        this.setState({
          pendingCompetitorData: false,
          suggestedCompetitors: [],
        })
      })
  }

  queryCompetitorAnalysis = (reportId) => {
    getCompetitorAnalysisReport(reportId)
      .then((res) => {
        const { pendingData, competitors } = res.data
        if (pendingData) {
          this.reportTimtoutId = setTimeout(() => {
            this.setState({
              suggestedCompetitors: competitors,
              pendingCompetitorData: pendingData,
            })
            this.queryCompetitorAnalysis(reportId)
          }, REPORT_POLL_DELAY)
        } else {
          this.setState({
            suggestedCompetitors: competitors,
            pendingCompetitorData: pendingData,
          })
        }
      })
      .catch((error) => {
        console.error('error', error)
        this.setState({ suggestedCompetitors: [] })
      })
  }

  pollSiteData = (domain, lang) => {
    getSiteData(domain, lang)
      .then((res) => {
        if (res.data && res.data.done) {
          this.setState(() => ({
            siteData: {
              isLoading: false,
              hasData: !!res.data.report,
              ...(res.data.report || {}),
            },
          }))
        } else {
          setTimeout(() => {
            this.pollSiteData(domain, lang)
          }, REPORT_POLL_DELAY)
        }
      })
      .catch((error) => {
        console.error('error', error)
        this.setState(() => ({
          siteData: {
            isLoading: false,
            hasData: false,
          },
        }))
      })
  }

  getSiteInformation = (domain, lang) => {
    this.setState(() => ({
      siteData: {
        isLoading: true,
      },
    }))

    this.pollSiteData(domain, lang)
  }

  getSeoPerformance = ({ domain, status }, isDomainOverview = true) => {
    if (isDomainOverview) {
      this.setState((prevState) => ({
        seoPerformance: {
          ...prevState.seoPerformance,
          domainOverview: {
            isLoading: true,
          },
        },
      }))
    } else if (status === VALID_STATUS.invalid) {
      this.setState((prevState) => {
        const { seoPerformance } = prevState
        const { competitors } = seoPerformance
        const index = competitors.findIndex((c) => c.domain === domain)
        if (index > -1) {
          competitors.splice(index, 1, {
            isLoading: false,
            domain,
            hasData: false,
          })
        } else {
          const { userProjects } = this.props
          const { MAX_PROJECTS_PER_USER, MAX_AVAILABLE_ADDON_PROJECTS } =
            getMaxValuesForUser()
          const totalProjectsCount = userProjects ? userProjects.length : 0
          const availableProjectsCount =
            MAX_PROJECTS_PER_USER - totalProjectsCount
          const newAddonProject =
            MAX_AVAILABLE_ADDON_PROJECTS > 0 &&
            availableProjectsCount === MAX_AVAILABLE_ADDON_PROJECTS
          const { MAX_COMPETITORS_PER_PROJECT } = getMaxValuesForProject(
            null,
            newAddonProject,
          )
          // Add only if it's not above the limit
          if (competitors.length < MAX_COMPETITORS_PER_PROJECT) {
            competitors.push({
              isLoading: false,
              domain,
              hasData: false,
            })
          }
        }
        return {
          seoPerformance: {
            ...seoPerformance,
            competitors: cloneDeep(competitors),
          },
        }
      })
      return
    }

    queryDomainOverview(domain, 2840, 'en', false)
      .then((res) => {
        if (isDomainOverview) {
          this.setState((prevState) => ({
            seoPerformance: {
              ...prevState.seoPerformance,
              domainOverview: {
                isLoading: false,
                hasData: true,
                ...res.data,
              },
            },
          }))
        } else {
          this.setState((prevState) => {
            const { seoPerformance } = prevState
            const { competitors } = seoPerformance
            const index = competitors.findIndex((c) => c.domain === domain)

            if (index > -1) {
              competitors.splice(index, 1, {
                isLoading: false,
                hasData: true,
                domain,
                ...res.data,
              })
            }

            return {
              seoPerformance: {
                ...prevState.seoPerformance,
                competitors: cloneDeep(competitors),
              },
            }
          })
        }
      })
      .catch((error) => {
        console.error('error', error)
        if (isDomainOverview) {
          this.setState((prevState) => ({
            seoPerformance: {
              ...prevState.seoPerformance,
              domainOverview: {
                isLoading: false,
                hasData: false,
                noData: true,
              },
            },
          }))
        } else {
          this.setState((prevState) => {
            const { seoPerformance } = prevState
            const { competitors } = seoPerformance
            const index = competitors.findIndex((c) => c.domain === domain)
            if (index > -1) {
              competitors.splice(index, 1, {
                isLoading: false,
                hasData: false,
                noData: true,
              })
            }
            return {
              seoPerformance: {
                ...prevState.seoPerformance,
                competitors: cloneDeep(competitors),
              },
            }
          })
        }
      })
  }

  getSiteAuditData = () => {
    const { domain } = this.state
    const { CRAWL_MAX_PAGES } = getMaxValuesForUser()

    startSiteAudit(getDomain(domain), undefined, CRAWL_MAX_PAGES, false, false)
  }

  submitStep1 = (domain) => {
    const { previousDomain } = this.state
    const transformedDomain = sanitizeDomain(domain)
    const isDomainChanged =
      previousDomain && previousDomain !== transformedDomain
    if (transformedDomain) {
      const defaultLoc = isDomainChanged ? [] : this.state.locations

      Mixpanel.trackProjectDomainInputSuccess(
        this.state.eventProps?.addProjectButtonLocation,
      )
      this.setState(
        (prevState) => ({
          domain: transformedDomain,
          availableKeywords: undefined,
          suggestedCompetitor: [],
          keywords: isDomainChanged ? [] : prevState.keywords,
          competitors: isDomainChanged ? [] : prevState.competitors,
          previousDomain: transformedDomain,
          locations: defaultLoc,
          eventProps: {
            ...prevState.eventProps,
            domain: transformedDomain,
          },
        }),
        () => {
          if (isSignedIn()) this.getSiteAuditData()

          this.getSeoPerformance({
            domain: transformedDomain,
            status: VALID_STATUS.valid,
          })
          this.stepper.nextStep()
        },
      )
    }
  }

  submitStep2 = (locations) => {
    const { eventProps: stateEventProps } = this.state
    const eventProps = {
      ...stateEventProps,
      locations: uniq(map(locations, 'name')),
      languages: uniq(map(locations, 'languageCode')),
    }
    Mixpanel.trackProjectLangLocAddSuccess(eventProps)

    const { domain } = this.state
    getAvailableKeywords(
      domain,
      locations.map((l) => generateKey([l.lang, l.parentId || l.id], ':')),
    ).then((response) => {
      this.setState({ availableKeywords: response.data.keywords })
    })

    if (!this.state.siteData.hasData) {
      const firstLocation = locations[0]
      this.getSiteInformation(domain, firstLocation.languageCode)
    }

    const competitorsLocations = filterCompetitorsLocations({
      locations,
      cachedLocations: this.props.cachedLocations,
    })
    this.setState({ locations, competitorsLocations, eventProps })
    this.getSuggestedCompetitors(
      domain,
      competitorsLocations[0].loc_id,
      competitorsLocations[0].lang,
    )
  }

  submitStep3 = (locations, skipped = false) => {
    const { eventProps: stateEventProps, keywords: stateKeywords } = this.state
    const keywords = skipped ? [] : stateKeywords
    const eventProps = {
      ...stateEventProps,
      keywords,
      skippedAddKeywords: skipped,
    }
    Mixpanel.trackProjectKeywordsAddSuccess(eventProps)

    this.setState({
      keywords,
      locations,
      eventProps,
    })

    const { domain, suggestedCompetitors, pendingCompetitorData } = this.state
    if (!pendingCompetitorData && suggestedCompetitors.length === 0) {
      const competitorsLocations = filterCompetitorsLocations({
        locations,
        cachedLocations: this.props.cachedLocations,
      })

      this.setState({ competitorsLocations })
      this.getSuggestedCompetitors(
        domain,
        competitorsLocations[0].loc_id,
        competitorsLocations[0].lang,
      )
    }
  }

  submitStep4 = (locations, skipped = false) => {
    const { eventProps: stateEventProps, competitors } = this.state
    const eventProps = {
      ...stateEventProps,
      competitors,
      skippedAddCompetitors: skipped,
    }
    Mixpanel.trackProjectCompetitorsAddSuccess(eventProps)
    this.setState((prevState) => ({
      competitorsLocations: locations,
      competitors: skipped ? [] : prevState.competitors,
      seoPerformance: {
        ...prevState.seoPerformance,
        competitors: skipped ? [] : prevState.seoPerformance.competitors,
      },
      eventProps,
    }))
  }

  submitStep6 = (leadsData, newAddonProject) => {
    if (leadsData) {
      this.trackNPDTeamGrowTrafficLeadSuccess({
        email: leadsData.email ?? '',
        phone: leadsData.phone ?? '',
        firstname: leadsData.firstname ?? '',
        lastname: leadsData.lastname ?? '',
        industry: leadsData.industry ?? '',
        budget: leadsData.budget ?? '',
        challenges: leadsData.challenges ?? '',
        urgency: leadsData.urgency ?? '',
        businessTypes: leadsData.business_types ?? '',
        numberOfEmployees: leadsData.employees ?? '',
        country: leadsData.country ?? '',
      })
    }

    const { userProjects } = this.props

    this.setState({ leadsData })

    BlockFeatureAction(
      DASHBOARD,
      'new_project',
      userProjects.length,
      () => {
        this.saveProjectWithLeadsData(leadsData, newAddonProject)
      },
      () => {
        this.handleBuyAddonInLastFlow()
      },
    )()
  }

  saveProjectWithLeadsData = (leadsData, newAddonProject) => {
    if (!isSignedIn()) {
      this.saveUnloggedUserProjectInfo()
      this.showConfirmModal({
        title: translate('last_screen_heading'),
        message: translate('last_screen_subheading'),
        buttonText: null,
        onCloseCallback: () => this.props.closeAddProjectModal(),
        showUpgrade: true,
        upgradeButtonLabel: translate('register'),
        redirectURL: '/register',
      })
    } else {
      const {
        domain,
        keywords,
        locations,
        competitorsLocations,
        competitors,
        rankTrackingFrequency,
        mobileRankTracking,
        eventProps,
      } = this.state

      useLeadCaptureStore.setState({ isLoading: true })

      const mappedLocations = mapLocLang(locations)
      const mappedCompetitorLocations = mapLocLang(competitorsLocations)
      const { MAX_KEYWORDS_PER_PROJECT } = getMaxValuesForProject(
        null,
        newAddonProject,
      )
      const keywordsAsConfig = {}
      if (locations.length > 0) {
        keywords.slice(0, MAX_KEYWORDS_PER_PROJECT).forEach((kw) => {
          keywordsAsConfig[kw] = cloneDeep(mappedLocations)
        })
      }

      const competitorsAsConfig = {}
      if (competitorsLocations.length > 0) {
        competitors.forEach((ct) => {
          competitorsAsConfig[ct.domain] = cloneDeep(mappedCompetitorLocations)
        })
      }

      this.props.addProject(
        {
          domain,
          title: domain,
          locations: mappedLocations,
          keywords: keywordsAsConfig,
          competitorsLocations: mappedCompetitorLocations,
          competitors: competitorsAsConfig,
          rankTrackingFrequency: rankTrackingFrequency.toUpperCase(),
          mobileRankTracking,
        },
        leadsData,
        ({ data, error }) => {
          if (data) {
            trackEvent({
              event: EVENTS.PROJECT_CREATION_SUCCESS,
              mixpanelProperties: [
                {
                  ...eventProps,
                  skippedAddKeywords: eventProps.skippedAddKeywords ?? false,
                  skippedAddCompetitors:
                    eventProps.skippedAddCompetitors ?? false,
                  agencyOfferResponse: !!leadsData,
                },
              ],
              kissmetricsProperties: {
                'project name': domain,
                'project URL': domain,
                location: generateKey(
                  [locations[0].languageCode, locations[0].id],
                  '_',
                ),
                'tracked keywords': keywords.length,
                'tracked competitors': competitors.length,
                'want agency help': !!leadsData,
              },
            })

            HubSpotService.instance.setTrackCustomBehavioralEvent({
              name: 'pe25525864_project_created',
              objectType: 'contacts',
            })

            if (!isEmpty(mappedLocations) && !isEmpty(keywordsAsConfig)) {
              HubSpotService.instance.setTrackCustomBehavioralEvent({
                name: 'pe25525864_rank_tracking',
                objectType: 'contacts',
              })
            }

            saveRecentProject(data)
            sendMessage('NEW_PROJECT_SAVED')
            useLeadCaptureStore.setState({
              isLoading: false,
              isModalOpen: !!leadsData,
              currentView: leadsData ? 'success' : 'initial',
            })

            const confirmModalData = {
              title: translate('step7_finish_heading'),
              message: translate('step7_finish_subheading'),
              buttonText: translate('go_to_dashboard'),
              onCloseCallback: () => {
                this.setState(this.getInitialState())
                this.props.onFinish(newAddonProject)
              },
            }

            if (!leadsData) {
              this.showConfirmModal(confirmModalData)
            } else {
              this.setState({ pendingConfirmModal: confirmModalData })
            }

            kissmetricsRecordingEvent(
              KISSMETRICS_TRACK_EVENTS.project_creation_actions,
              {
                'project flow action': 'grow traffic congrats',
              },
            )

            removeUnloggedUserProject()
            removeUnloggedUserProjectCheckout()
          } else {
            useLeadCaptureStore.setState({
              isLoading: false,
              currentView: 'initial',
            })
            console.error(error)
            this.showConfirmModal({
              title: translate('error'),
              message: translate(translate('invalid_domain')),
              buttonText: translate('ok'),
              onCloseCallback: () => {
                this.setState({
                  isStepWizardOpen: true,
                  confirmModal: {
                    isOpen: false,
                  },
                })
                this.stepper.firstStep()
              },
            })
          }
        },
      )
    }
  }

  handleBuyAddonInLastFlow = async () => {
    const { user } = this.props
    const domainAddon = getAddonByKey(user.addons, allAddonsTypes.ADDON_DOMAIN)
    const canBuyAddon = isPaidUser() && !isSubUser(user)
    const { invalidDomain } = await this.validateStep1()

    if (invalidDomain === translate('url_already_used')) {
      return this.stepper.firstStep()
    }

    this.showPromptModal({
      title: translate('project_prompt_headline'),
      message: translate(
        canBuyAddon && domainAddon
          ? 'project_limit_addon_offer2'
          : 'project_prompt_text',
      ),
      eventProperty: {
        [PROPERTY_NAMES.btn_location]:
          PROPERTY_VALUES.dashboard.new_project_header,
        btnLocation:
          MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.PROJECT_CREATION_FLOW,
      },
      forceUpgrade: true,
      from: 'add_project_modal_with_leads_data',
      addonType:
        canBuyAddon && domainAddon ? allAddonsTypes.ADDON_DOMAIN : null,
    })

    if (!(canBuyAddon && domainAddon)) {
      this.setState({ isFreeUser: true })
    }
  }

  handleBuyAddon = async () => {
    const { user } = this.props
    const domainAddon = getAddonByKey(user.addons, allAddonsTypes.ADDON_DOMAIN)
    const canBuyAddon = isPaidUser() && !isSubUser(user)
    const { invalidDomain } = await this.validateStep1()
    this.setState({
      saveModal: {
        isOpen: false,
      },
    })

    if (invalidDomain === translate('url_already_used')) {
      return this.stepper.firstStep()
    }

    this.showPromptModal({
      title: translate('project_prompt_headline'),
      message: translate(
        canBuyAddon && domainAddon
          ? 'project_limit_addon_offer2'
          : 'project_prompt_text',
      ),
      eventProperty: {
        [PROPERTY_NAMES.btn_location]:
          PROPERTY_VALUES.dashboard.new_project_header,
        btnLocation:
          MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.PROJECT_CREATION_FLOW,
      },
      forceUpgrade: true,
      from: 'add_project_modal',
      addonType:
        canBuyAddon && domainAddon ? allAddonsTypes.ADDON_DOMAIN : null,
    })
  }

  handleSaveIncompletedProject = (newAddonProject) => {
    this.setState((prevState) => ({
      saveModal: {
        ...prevState.saveModal,
        isLoading: true,
      },
    }))

    const { userProjects } = this.props

    BlockFeatureAction(
      DASHBOARD,
      'new_project',
      userProjects.length,
      () => {
        this.saveProject(newAddonProject)
      },
      () => {
        this.handleBuyAddon()
      },
    )()
  }

  saveProject = async (newAddonProject) => {
    const { invalidDomain } = await this.validateStep1()
    if (!invalidDomain) {
      if (!isSignedIn()) {
        this.saveUnloggedUserProjectInfo()
        this.showConfirmModal({
          title: translate('last_screen_heading'),
          message: translate('last_screen_subheading'),
          buttonText: null,
          onCloseCallback: () => this.props.closeAddProjectModal(),
          showUpgrade: true,
          upgradeButtonLabel: translate('register'),
          redirectURL: '/register',
        })
      } else {
        const {
          domain,
          keywords,
          locations,
          competitorsLocations,
          competitors,
          rankTrackingFrequency,
          mobileRankTracking,
          eventProps,
          activeStep,
        } = this.state

        const { MAX_KEYWORDS_PER_PROJECT } = getMaxValuesForProject(
          null,
          newAddonProject,
        )
        const mappedLocations = mapLocLang(locations)
        const mappedCompetitorLocations = mapLocLang(competitorsLocations)

        const keywordsAsConfig = {}
        if (locations.length > 0) {
          keywords.slice(0, MAX_KEYWORDS_PER_PROJECT).forEach((kw) => {
            keywordsAsConfig[kw] = cloneDeep(mappedLocations)
          })
        }

        const competitorsAsConfig = {}
        if (competitorsLocations.length > 0) {
          competitors.forEach((ct) => {
            competitorsAsConfig[ct.domain] = cloneDeep(
              mappedCompetitorLocations,
            )
          })
        }

        this.props.addProject(
          {
            domain: sanitizeDomain(domain),
            title: sanitizeDomain(domain),
            locations: mappedLocations,
            keywords: keywordsAsConfig,
            competitorsLocations: mappedCompetitorLocations,
            competitors: competitorsAsConfig,
            rankTrackingFrequency: rankTrackingFrequency.toUpperCase(),
            mobileRankTracking,
          },
          null,
          ({ data, error }) => {
            if (data) {
              if (domain !== (eventProps.domain ?? '') && activeStep === 1) {
                Mixpanel.trackProjectDomainInputSuccess(
                  eventProps?.addProjectButtonLocation,
                )
              }
              if (
                !isEqual(
                  uniq(map(locations, 'name')),
                  eventProps.locations ?? [],
                ) &&
                activeStep === 2
              ) {
                Mixpanel.trackProjectLangLocAddSuccess({
                  ...eventProps,
                  locations: uniq(map(locations, 'name')),
                  languages: uniq(map(locations, 'languageCode')),
                })
              }
              if (
                !isEqual(keywords, eventProps.keywords ?? []) &&
                activeStep === 3
              ) {
                Mixpanel.trackProjectKeywordsAddSuccess({
                  ...eventProps,
                  locations: uniq(map(locations, 'name')),
                  languages: uniq(map(locations, 'languageCode')),
                  keywords,
                  skippedAddKeywords: !keywords.length,
                })
              }
              if (
                !isEqual(competitors, eventProps.competitors ?? []) &&
                activeStep === 4
              ) {
                Mixpanel.trackProjectCompetitorsAddSuccess({
                  ...eventProps,
                  locations: uniq(map(locations, 'name')),
                  languages: uniq(map(locations, 'languageCode')),
                  competitors,
                  skippedAddCompetitors: !competitors.length,
                })
              }

              trackEvent({
                event: EVENTS.PROJECT_CREATION_SUCCESS,
                mixpanelProperties: [
                  {
                    ...eventProps,
                    locations: uniq(map(locations, 'name')),
                    languages: uniq(map(locations, 'languageCode')),
                    keywords,
                    skippedAddKeywords: !keywords.length,
                    competitors,
                    skippedAddCompetitors: !competitors.length,
                    agencyOfferResponse: false,
                  },
                ],
                kissmetricsProperties: {
                  'project name': sanitizeDomain(domain),
                  'project URL': domain,
                  location:
                    locations.length > 0
                      ? generateKey(
                          [locations[0].languageCode, locations[0].id],
                          '_',
                        )
                      : null,
                  'tracked keywords': keywords.length,
                  'tracked competitors': competitors.length,
                  'want agency help': false,
                },
              })

              HubSpotService.instance.setTrackCustomBehavioralEvent({
                name: 'pe25525864_project_created',
                objectType: 'contacts',
              })

              if (!isEmpty(mappedLocations) && !isEmpty(keywordsAsConfig)) {
                HubSpotService.instance.setTrackCustomBehavioralEvent({
                  name: 'pe25525864_rank_tracking',
                  objectType: 'contacts',
                })
              }

              saveRecentProject(data)
              this.setState(this.getInitialState(), () => {
                sendMessage('NEW_PROJECT_SAVED')
              })
              this.props.onFinish(newAddonProject)
            } else {
              this.setState({
                saveModal: {
                  isOpen: false,
                },
              })
              if (error.message === 'MAXIMUM_AMOUNT_OF_PROJECTS') {
                this.handleBuyAddon()
                return
              }
              const errorMessage = translate(
                error.message === 'PROJECT_ALREADY_EXISTS'
                  ? 'domain_already_used'
                  : 'unexpected_error_messaging',
              )

              this.showConfirmModal({
                title: translate('error'),
                message: errorMessage,
                buttonText: translate('ok'),
                onCloseCallback: () => {
                  this.setState({
                    isStepWizardOpen: true,
                    confirmModal: {
                      isOpen: false,
                    },
                  })
                  this.stepper.firstStep()
                },
              })
            }
          },
        )
      }
    } else {
      if (invalidDomain === translate('url_already_used')) {
        this.stepper.firstStep()
      }

      this.setState({
        saveModal: {
          isOpen: false,
        },
      })
    }
  }

  showPromptModal = ({
    title,
    message,
    eventProperty,
    from,
    forceUpgrade = false,
    addonType = null,
  }) => {
    if ((!forceUpgrade && isPaidUser()) || isLimitDisabled()) return
    sendMessage('SHOW_PROMPT', {
      title,
      message,
      eventProperty,
      forceUpgrade,
      from,
      addonType,
    })
  }

  handleChange = (name) => (e) => {
    const invalidMap = {
      domain: 'invalidDomain',
    }
    this.setState({
      [name]: e.target.value,
      [invalidMap[name]]: false,
    })
  }

  handleChangeValue = (property) => (value) =>
    this.setState({
      [property]: value,
    })

  trackWantNPDTeamGrowTrafficClick = () => {
    const { locations, keywords, competitors, eventProps } = this.state
    Mixpanel.trackWantNPDTeamGrowTrafficClick({
      ...eventProps,
      locations: uniq(map(locations, 'name')),
      languages: uniq(map(locations, 'languageCode')),
      competitors,
      keywords,
      skippedAddKeywords: !keywords.length,
      skippedAddCompetitors: !competitors.length,
    })
  }

  trackChooseYourSuccessPathView = () => {
    const { locations, keywords, competitors, eventProps } = this.state
    trackEvent({
      event: EVENTS.CHOOSE_YOUR_SUCCESS_PATH_VIEW,
      mixpanelProperties: [
        {
          ...eventProps,
          locations: uniq(map(locations, 'name')),
          languages: uniq(map(locations, 'languageCode')),
          competitors,
          keywords,
          skippedAddKeywords: !keywords.length,
          skippedAddCompetitors: !competitors.length,
        },
      ],
    })
  }

  trackChooseYourSuccessPathCTAClicked = (buttonLocation) => {
    const { locations, keywords, competitors, eventProps } = this.state
    trackEvent({
      event: EVENTS.CHOOSE_YOUR_SUCCESS_PATH_CTA_CLICKED,
      mixpanelProperties: [
        {
          ...eventProps,
          locations: uniq(map(locations, 'name')),
          languages: uniq(map(locations, 'languageCode')),
          competitors,
          keywords,
          skippedAddKeywords: !keywords.length,
          skippedAddCompetitors: !competitors.length,
          onboardingSuccessPath: buttonLocation,
        },
      ],
    })
  }

  trackNPDTeamGrowTrafficLeadSuccess = ({
    businessTypes,
    challenges,
    numberOfEmployees,
    urgency,
    industry,
    budget,
    phone,
    email,
    firstname,
    lastname,
    country,
  }) => {
    const { locations, keywords, competitors, eventProps } = this.state
    Mixpanel.trackNPDTeamGrowTrafficLeadSuccess({
      ...eventProps,
      locations: uniq(map(locations, 'name')),
      languages: uniq(map(locations, 'languageCode')),
      competitors,
      keywords,
      skippedAddKeywords: !keywords.length,
      skippedAddCompetitors: !competitors.length,
      businessTypes,
      challenges,
      numberOfEmployees,
      urgency,
      industry,
      budget,
      phone,
      email,
      firstname,
      lastname,
      country,
    })
  }

  validateStep1 = () => {
    if (this.state.domain.trim() === '') {
      const invalidDomain = translate('error_domain_url_input')
      this.setState({
        invalidDomain,
      })

      return new Promise((resolve) => {
        resolve({
          invalidDomain,
        })
      })
    }

    const sanitizedDomain = sanitizeDomain(this.state.domain)
    const getInvalidDomain = () => {
      if (!sanitizedDomain) {
        return translate('invalid_domain')
      }
      return this.props.userProjects.some(
        (project) => project.domain === sanitizedDomain,
      )
        ? translate('url_already_used')
        : null
    }
    let invalidDomain = getInvalidDomain()

    if (invalidDomain) {
      this.setState({
        invalidDomain,
      })
      return new Promise((resolve) => {
        resolve({
          invalidDomain,
        })
      })
    }

    return validateSite(sanitizedDomain, true, true)
      .then((res) => {
        invalidDomain = !res.data.is_valid
          ? translate('site_not_reached')
          : null
        this.setState({
          invalidDomain,
        })
        return { invalidDomain }
      })
      .catch((error) => {
        console.error('error', error)
        invalidDomain = translate('site_not_reached')
        this.setState({
          invalidDomain,
        })
        return { invalidDomain }
      })
  }

  validateStep2 = () => {
    const invalidLocations = !this.state.locations.length

    if (invalidLocations) {
      this.setState({ invalidLocations })
    }
    return { invalidLocations }
  }

  setKeywords = (keywords) => this.setState({ keywords })

  setCompetitors = (newCompetitors) => {
    const { userProjects } = this.props
    const { MAX_PROJECTS_PER_USER, MAX_AVAILABLE_ADDON_PROJECTS } =
      getMaxValuesForUser()
    const totalProjectsCount = userProjects ? userProjects.length : 0
    const availableProjectsCount = MAX_PROJECTS_PER_USER - totalProjectsCount
    const newAddonProject =
      MAX_AVAILABLE_ADDON_PROJECTS > 0 &&
      availableProjectsCount === MAX_AVAILABLE_ADDON_PROJECTS

    const { MAX_COMPETITORS_PER_PROJECT } = getMaxValuesForProject(
      null,
      newAddonProject,
    )
    const validCompetitors = newCompetitors.slice(
      0,
      MAX_COMPETITORS_PER_PROJECT,
    ) // do not get seo performance of the competitors exceeding max competitor count per user

    const notValidatedCompetitors = newCompetitors
      .filter((competitor) => competitor.status === VALID_STATUS.default)
      .map((competitor) => ({
        ...competitor,
        status: VALID_STATUS.validating,
      }))

    const seoCompetitors = this.state.seoPerformance.competitors.filter((sc) =>
      validCompetitors.some((nc) => nc.domain === sc.domain),
    )

    // Add SEO Performance metrics for newly added suggested competitors
    validCompetitors
      .filter((vc) => !seoCompetitors.some((sc) => sc.domain === vc.domain))
      .forEach((vc) => {
        const suggestedCompetitor = this.state.suggestedCompetitors.find(
          (sc) => sc.domain === vc.domain,
        )
        if (suggestedCompetitor) {
          seoCompetitors.push({
            ...suggestedCompetitor,
            isLoading: false,
            hasData: true,
          })
        } else {
          seoCompetitors.push({
            domain: vc.domain,
            isLoading: vc.status === VALID_STATUS.validating,
            hasData: false,
          })
        }
      })

    this.setState(
      (prevState) => ({
        competitors: newCompetitors,
        // remove seoPerformance competitors if new competitors doesn't have it
        seoPerformance: {
          ...prevState.seoPerformance,
          competitors: seoCompetitors,
        },
      }),
      () => {
        const {
          seoPerformance: { competitors },
        } = this.state
        competitors.forEach((c) => {
          if (c.hasData === false) {
            this.getSeoPerformance(c, false)
          }
        })
        Promise.all(
          notValidatedCompetitors.map((c) =>
            validateSite(getDomain(c.domain), true)
              .then((res) => ({
                ...c,
                status: res.data.is_valid
                  ? VALID_STATUS.valid
                  : VALID_STATUS.invalid,
              }))
              .catch((error) => {
                console.error('error', error)
                return {
                  ...c,
                  status: VALID_STATUS.invalid,
                }
              }),
          ),
        ).then((validatedCompetitors) => {
          const { competitors: stateCompetitors } = this.state
          validatedCompetitors.forEach((vc) => {
            const index = findIndex(stateCompetitors, { domain: vc.domain })
            stateCompetitors.splice(index, 1, vc)
            this.getSeoPerformance(vc, false)
          })
          this.setState({
            competitors: cloneDeep(stateCompetitors),
          })
        })
      },
    )
  }

  rollbackLocations = () => {
    // should rollback location selection
    this.setState((prevState) => ({
      locations: cloneDeep(prevState.previousLocations),
      previousLocations: [],
    }))
  }

  addLocation = (localeInfo) => {
    const { locations } = this.state
    const newLocations = uniqBy(
      [...this.state.locations, localeInfo],
      ({ id, languageCode }) => generateKey([id, languageCode]),
    )

    const reachedLimit =
      newLocations.length > getMaxValuesForProject().MAX_LOCATIONS_PER_PROJECT

    if (reachedLimit) {
      const previousLocations = cloneDeep(locations)
      this.setState({
        locations: newLocations,
        previousLocations,
        locSearch: '',
      })

      this.showUpgradeModal({
        title: 'heading_new_location',
        reachedText: 'step2_reached_location_limit',
        locations: newLocations,
        previousLocations,
      })
    } else {
      this.setState({
        locations: newLocations,
        locSearch: '',
      })
    }
  }

  removeLocation = (location) => {
    const { locations, siteData: stateSiteData } = this.state
    const newLocations = locations.filter(
      (loc) =>
        loc.id !== location.id || loc.languageCode !== location.languageCode,
    )
    const siteData =
      newLocations.length > 0
        ? stateSiteData
        : {
            isLoading: false,
          }
    this.setState({
      locations: newLocations,
      siteData,
    })
  }

  updateStep = ({ previousStep, activeStep }) => {
    if (activeStep === 1 && previousStep === 2) {
      this.setState({
        activeStep,
        seoPerformance: {
          domainOverview: {
            isLoading: false,
          },
          competitors: [],
        },
        siteData: {
          isLoading: false,
        },
      })
    } else {
      this.setState({ activeStep })
    }
    this.scrollToTop()
  }

  showUpgradeModal = ({ title, reachedText, locations, previousLocations }) => {
    trackEvent({
      event: EVENTS.PURCHASE_CLICK,
      mixpanelProperties: [
        MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.PROJECT_CREATION_FLOW,
        isPaidUser() || isFreeTrialUsed()
          ? MIXPANEL_PROPS.BUTTON_CTA.values.UPGRADE
          : MIXPANEL_PROPS.BUTTON_CTA.values.START_TRIAL,
        MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
      ],
    })
    trackEvent({ event: EVENTS.NEW_CHECKOUT_MODAL_VIEW })
    if (!isSignedIn()) {
      this.saveUnloggedUserProjectInfoCheckout({
        locations,
        previousLocations,
      })
      this.setState({
        isSignupModalOpen: true,
        titleUpgradePlan: title,
        reachedText,
      })
      return
    }

    this.setState({
      isCheckoutModalOpen: true,
      titleUpgradePlan: title,
      reachedText,
    })
  }

  showConfirmModal = ({
    title,
    message,
    buttonText,
    onCloseCallback,
    upgradeButtonLabel,
    redirectURL,
    showUpgrade = false,
  }) => {
    this.setState({
      isStepWizardOpen: false,
      saveModal: {
        isOpen: false,
      },
      confirmModal: {
        isOpen: true,
        title,
        message,
        buttonText,
        onCloseCallback,
        showUpgrade,
        upgradeButtonLabel,
        redirectURL,
      },
    })
  }

  showEditLocationsModal = (onSubmitCallback, onCloseCallback) => {
    const {
      activeStep,
      locations: stateLocations,
      competitorsLocations,
    } = this.state
    const title =
      activeStep === 3
        ? translate('edit_kw_loc')
        : translate('edit_competitor_loc')
    const locations = activeStep === 3 ? stateLocations : competitorsLocations
    this.setState({
      editLocationsModal: {
        isOpen: true,
        title,
        locations,
        onSubmitCallback,
        onCloseCallback,
      },
    })
  }

  hideEditLocationsModal = () => {
    this.setState({
      editLocationsModal: {
        isOpen: false,
      },
    })
  }

  updateLocations = (newLocations) => {
    const { activeStep, eventProps: stateEventProps, locations } = this.state
    const { MAX_LOCATIONS_PER_PROJECT, MAX_COMPETITORS_LOCATIONS_PER_PROJECT } =
      getMaxValuesForProject()

    const reachedLimit =
      activeStep === 3
        ? newLocations.length > MAX_LOCATIONS_PER_PROJECT
        : newLocations.length > MAX_COMPETITORS_LOCATIONS_PER_PROJECT

    if (reachedLimit) {
      const previousLocations = cloneDeep(locations)
      this.setState({
        locations: newLocations,
        previousLocations,
      })

      this.setState(
        {
          editLocationsModal: {
            isOpen: false,
          },
        },
        () => {
          this.showUpgradeModal({
            title: 'heading_new_location',
            reachedText: 'step2_reached_location_limit',
            locations: newLocations,
            previousLocations,
          })
        },
      )
    } else {
      const oldLocations = cloneDeep(
        this.state[activeStep === 3 ? 'locations' : 'competitorsLocations'],
      )
      const eventLocations =
        activeStep === 3
          ? [...newLocations, ...this.state.competitorsLocations]
          : [...this.state.locations, ...newLocations]
      const eventProps = {
        ...stateEventProps,
        locations: uniq(map(eventLocations, 'name')),
        languages: uniq(map(eventLocations, 'languageCode')),
      }
      this.setState(
        {
          editLocationsModal: {
            isOpen: false,
          },
          [activeStep === 3 ? 'locations' : 'competitorsLocations']:
            newLocations,
          eventProps,
        },
        () => {
          if (
            activeStep === 3 &&
            !isEqual(
              newLocations.map((l) => generateKey([l.lang, l.loc_id])),
              oldLocations.map((l) => generateKey([l.lang, l.loc_id])),
            )
          ) {
            getAvailableKeywords(
              this.state.domain,
              newLocations.map((l) =>
                generateKey([l.lang, l.parentId || l.loc_id], ':'),
              ),
            ).then((response) => {
              this.setState({ availableKeywords: response.data.keywords })
            })
          } else if (
            activeStep === 4 &&
            !isEqual(
              generateKey([newLocations[0].lang, newLocations[0].loc_id]),
              generateKey([oldLocations[0]?.lang, oldLocations[0]?.loc_id]),
            )
          ) {
            setTimeout(this.reportTimtoutId)
            this.getSuggestedCompetitors(
              this.state.domain,
              newLocations[0].loc_id,
              newLocations[0].lang,
            )
          }
        },
      )
    }
  }

  saveUnloggedUserProjectInfo = (tier, plan) => {
    const {
      domain,
      keywords,
      availableKeywords,
      locations,
      competitors,
      competitorsLocations,
      locSearch,
      rankTrackingFrequency,
      mobileRankTracking,
      growYourTraffic,
      leadsData,
      activeStep,
      seoPerformance,
      siteData,
      eventProps,
    } = this.state

    const project = {
      domain,
      availableKeywords,
      keywords,
      competitors,
      competitorsLocations,
      locations,
      locSearch,
      rankTrackingFrequency,
      mobileRankTracking,
      growYourTraffic,
      leadsData,
      activeStep,
      tier,
      plan,
      seoPerformance,
      siteData,
      eventProps,
    }

    saveUnloggedUserProject(project)
  }

  saveUnloggedUserProjectInfoCheckout = (data = {}) => {
    const {
      domain,
      keywords,
      availableKeywords,
      locations,
      competitors,
      competitorsLocations,
      locSearch,
      rankTrackingFrequency,
      mobileRankTracking,
      growYourTraffic,
      leadsData,
      activeStep,
      seoPerformance,
      siteData,
      eventProps,
      previousLocations,
    } = this.state

    const project = {
      domain,
      availableKeywords,
      keywords,
      competitors,
      competitorsLocations,
      locSearch,
      rankTrackingFrequency,
      mobileRankTracking,
      growYourTraffic,
      leadsData,
      activeStep,
      seoPerformance,
      siteData,
      eventProps,
      locations: data.locations || locations,
      previousLocations: data.previousLocations || previousLocations,
    }

    saveUnloggedUserProjectCheckout(project)
  }

  handleCloseButton = () => {
    if (!this.state.domain || this.state.invalidDomain) {
      this.closeAddProjectModalWithoutSaving()
    } else {
      this.setState({
        saveModal: {
          isOpen: true,
          title: translate('save_project'),
          message: translate('save_project_subheading'),
        },
      })
    }
  }

  closeAddProjectModalWithoutSaving = () => {
    const { search } = history.location
    this.props.onRequestClose()

    if (search.includes('from=ai_writer')) {
      const { next } = parse(search)
      const [nextUrl] = next ? next.split('?') : [AIWRITER_BASE_URL]
      window.location.href = nextUrl
    }
  }

  scrollToTop = () => {
    const parentDom = this.contentRef?.current?.parentElement
    if (parentDom) {
      parentDom.scrollTop = 0
    }
  }

  handleCloseCheckoutModal = async () => {
    const { MAX_LOCATIONS_PER_PROJECT } = this.getMaxValuesForProject()

    if (
      this.state.reachedText === 'step2_reached_location_limit' &&
      this.state.previousLocations?.length &&
      this.state.locations.length > MAX_LOCATIONS_PER_PROJECT
    ) {
      this.rollbackLocations()
    }

    this.setState({
      isSignupModalOpen: false,
      isCheckoutModalOpen: false,
      titleUpgradePlan: '',
      reachedText: '',
    })

    const { invalidDomain } = await this.validateStep1()

    if (invalidDomain === translate('url_already_used')) {
      this.stepper.firstStep()
    }
  }

  getMaxLimitReachedText = ({
    MAX_KEYWORDS_PER_PROJECT,
    MAX_COMPETITORS_PER_PROJECT,
  }) => {
    if (this.state.reachedText === 'step3_reached_maximum_keyword') {
      return MAX_KEYWORDS_PER_PROJECT
    }

    if (this.state.reachedText === 'step4_reached_maximum_competitors') {
      return MAX_COMPETITORS_PER_PROJECT
    }

    return null
  }

  getMaxLimitTitle = () => {
    if (this.state.titleUpgradePlan === 'heading_new_keywords') {
      return this.props.newLimitsChangingPlans.keywords
    }

    if (this.state.titleUpgradePlan === 'heading_new_competitors') {
      return this.props.newLimitsChangingPlans.competitors
    }

    return null
  }

  renderCloseButton = (shouldDisableButton) => (
    <Button
      variant="legacy-reload"
      data-testid="add-project-modal-close"
      className="tw-relative tw-bottom-2 tw-left-5 tw-border-none"
      onClick={this.handleCloseButton}
      disabled={shouldDisableButton}
    >
      <IconX />
    </Button>
  )

  getDisableTooltipText = (
    keywordsLength,
    maxKeywordsLength,
    competitorsLength,
    maxCompetitorsLength,
  ) => {
    if (keywordsLength > maxKeywordsLength) {
      return translate('reduce_keywords_or_locations', maxKeywordsLength)
    }
    if (competitorsLength > maxCompetitorsLength) {
      return translate('reduce_competitors', maxCompetitorsLength)
    }
    return ''
  }

  render() {
    const { user, userProjects, ...rest } = this.props
    const {
      isStepWizardOpen,
      activeStep,
      availableKeywords,
      pendingCompetitorData,
      suggestedCompetitors,
      confirmModal,
      saveModal,
      editLocationsModal,
      growYourTraffic,
      eventProps,
      reachedText,
      titleUpgradePlan,
      isCheckoutModalOpen,
      isSignupModalOpen,
      siteData,
      seoPerformance,
      domain,
      isFreeUser,
      competitorsLocations,
      competitors,
      locations,
      keywords,
      invalidDomain,
      invalidLocations,
      locSearch,
    } = this.state
    const { MAX_PROJECTS_PER_USER, MAX_AVAILABLE_ADDON_PROJECTS } =
      getMaxValuesForUser()
    const totalProjectsCount = userProjects ? userProjects.length : 0
    const availableProjectsCount = MAX_PROJECTS_PER_USER - totalProjectsCount
    const newAddonProject =
      MAX_AVAILABLE_ADDON_PROJECTS > 0 &&
      availableProjectsCount === MAX_AVAILABLE_ADDON_PROJECTS

    const {
      MAX_LOCATIONS_PER_PROJECT,
      MAX_KEYWORDS_PER_PROJECT,
      MAX_COMPETITORS_PER_PROJECT,
      MAX_COMPETITORS_LOCATIONS_PER_PROJECT,
    } = this.getMaxValuesForProject()

    const shouldDisableButton =
      keywords.length * locations.length > MAX_KEYWORDS_PER_PROJECT ||
      competitors.length * competitorsLocations.length >
        MAX_COMPETITORS_PER_PROJECT

    return (
      <Modal
        testId="add-project-modal"
        fitContent={confirmModal.isOpen}
        desktopStyle={desktopStyle}
        overlayStyle={{ zIndex: 10000 }}
        mobileStyle={mobileStyle(confirmModal.isOpen)}
        parentSelector={() =>
          document.getElementById('prompt-modal-create-project')
        }
        {...rest}
      >
        <ModalHeader
          noBorder
          style={{
            display: 'flex',
            padding: '20px',
            borderBottom: `2px solid ${palette.lightGray}`,
            background: palette.white,
            width: '100%',
          }}
        >
          <ModalHeaderContent>
            <NavLogoImg
              src={logo}
              alt="Logo"
              width={150}
              onClick={() => replaceWithLanguageRoutes('/dashboard')}
              style={{ borderRight: 'none', cursor: 'default' }}
            />

            <CreteProjectText
              className="tw-hidden xs:tw-flex"
              data-testid="creation-flow-title"
            >
              {translate('project_flow_create_project')}
            </CreteProjectText>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {shouldDisableButton ? (
                <Tooltip
                  text={this.getDisableTooltipText(
                    keywords.length * locations.length,
                    MAX_KEYWORDS_PER_PROJECT,
                    competitors.length * competitorsLocations.length,
                    MAX_COMPETITORS_PER_PROJECT,
                  )}
                  content={this.renderCloseButton(shouldDisableButton)}
                  place="left"
                  zIndex={999999}
                  distance={-25}
                />
              ) : (
                this.renderCloseButton(shouldDisableButton)
              )}
            </div>
          </ModalHeaderContent>
        </ModalHeader>
        <ModalContent
          styles={{
            position: 'relative',
            padding: 0,
            flexGrow: 1,
          }}
          ref={this.contentRef}
        >
          <Content hidden={!isStepWizardOpen}>
            <Stepper
              activeStep={activeStep}
              shouldRenderNav={(activeStepParam) =>
                activeStepParam <= stepsWithGrow.length
              }
              steps={growYourTraffic ? stepsWithGrow : stepsWithoutGrow}
              onStepChange={this.updateStep}
              instance={(stepper) => {
                this.stepper = stepper
              }}
              styles={{
                padding: '40px 20px 0px',
                width: '60%',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                height: 0,
                minHeight: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                margin: '0px auto',
              }}
            >
              <Step1
                onSubmit={this.submitStep1}
                userProjects={userProjects}
                onClose={this.closeAddProjectModalWithoutSaving}
                domain={domain}
                handleChange={this.handleChange}
                validate={this.validateStep1}
                invalidDomain={invalidDomain}
                eventProps={eventProps}
              />
              <Step2
                onSubmit={this.submitStep2}
                MAX_LOCATIONS_PER_PROJECT={MAX_LOCATIONS_PER_PROJECT}
                allLocations={this.locations}
                locations={locations}
                locSearch={locSearch}
                addLocation={this.addLocation}
                removeLocation={this.removeLocation}
                handleChange={this.handleChange}
                invalidLocations={invalidLocations}
                validate={this.validateStep2}
                showUpgradeModal={() =>
                  this.showUpgradeModal({
                    title: 'heading_new_location',
                  })
                }
              />
              <Step3
                onSubmit={this.submitStep3}
                MAX_KEYWORDS_PER_PROJECT={MAX_KEYWORDS_PER_PROJECT}
                availableKeywords={availableKeywords}
                keywords={keywords}
                setKeywords={this.setKeywords}
                locations={locations}
                showUpgradeModal={(reachedTextContent) =>
                  this.showUpgradeModal({
                    title: 'heading_new_keywords',
                    reachedText: reachedTextContent,
                  })
                }
                newAddonProject={newAddonProject}
                openEditLocationsModal={this.showEditLocationsModal}
              />
              <Step4
                domain={domain}
                MAX_COMPETITORS_PER_PROJECT={MAX_COMPETITORS_PER_PROJECT}
                MAX_COMPETITORS_LOCATIONS_PER_PROJECT={
                  MAX_COMPETITORS_LOCATIONS_PER_PROJECT
                }
                onSubmit={this.submitStep4}
                pendingData={pendingCompetitorData}
                suggestedCompetitors={suggestedCompetitors}
                competitors={competitors}
                setCompetitors={this.setCompetitors}
                locations={competitorsLocations}
                showUpgradeModal={(reachedTextContent) =>
                  this.showUpgradeModal({
                    reachedText: reachedTextContent,
                    title: 'heading_new_competitors',
                  })
                }
                newAddonProject={newAddonProject}
                openEditLocationsModal={this.showEditLocationsModal}
                submitStep6={(leadsData) =>
                  this.submitStep6(leadsData, newAddonProject)
                }
                trackChooseYourSuccessPathView={
                  this.trackChooseYourSuccessPathView
                }
                trackChooseYourSuccessPathCTAClicked={
                  this.trackChooseYourSuccessPathCTAClicked
                }
              />
              <Step6
                onSubmit={(leadsData) =>
                  this.submitStep6(leadsData, newAddonProject)
                }
                growYourTraffic={growYourTraffic}
                handleChangeValue={this.handleChangeValue}
                trackWantNPDTeamGrowTrafficClick={
                  this.trackWantNPDTeamGrowTrafficClick
                }
                trackNPDTeamGrowTrafficLeadSuccess={
                  this.trackNPDTeamGrowTrafficLeadSuccess
                }
                user={user}
                isFreeUser={isFreeUser}
                isOpen={isStepWizardOpen}
              />
            </Stepper>
            <SiteInsightPreview
              domain={domain}
              step={activeStep}
              seoPerformance={seoPerformance}
              siteData={siteData}
            />
          </Content>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ConfirmationModal
              hideFooter
              isOpen={confirmModal.isOpen}
              title={confirmModal.title}
              message={confirmModal.message}
              overlayStyle={{ zIndex: 100002 }}
              onClose={confirmModal.onCloseCallback}
              showUpgrade={confirmModal.showUpgrade}
              redirectURL={confirmModal.redirectURL}
              upgradeButtonLabel={confirmModal.upgradeButtonLabel}
            />
            <ConfirmationModal
              isLoading={saveModal.isLoading}
              isOpen={saveModal.isOpen}
              title={saveModal.title}
              overlayStyle={{ zIndex: 100002 }}
              message={saveModal.message}
              onAccept={() => {
                this.handleSaveIncompletedProject(newAddonProject)
              }}
              onDeny={() => {
                removeUnloggedUserProject()
                removeUnloggedUserProjectCheckout()
                this.closeAddProjectModalWithoutSaving()
              }}
            />
            <EditLocationsModal
              isOpen={editLocationsModal.isOpen}
              step={activeStep}
              title={editLocationsModal.title}
              locations={editLocationsModal.locations}
              onSubmit={this.updateLocations}
              onCancel={this.hideEditLocationsModal}
              overlayStyle={{ zIndex: 100002 }}
              showUpgradeModal={(reachedTextContent) =>
                this.showUpgradeModal({
                  reachedText: reachedTextContent,
                  title: 'heading_new_location',
                })
              }
            />
            <SignupModal
              isOpen={isSignupModalOpen}
              onClose={this.handleCloseCheckoutModal}
              onAuthSuccess={() =>
                this.setState({
                  isSignupModalOpen: false,
                  isCheckoutModalOpen: true,
                })
              }
              searchType={titleUpgradePlan}
              text={reachedText}
              isAddProjectFlow
              maxLimitMessage={{
                title: this.getMaxLimitTitle(),
                reachedText: this.getMaxLimitReachedText({
                  MAX_KEYWORDS_PER_PROJECT,
                  MAX_COMPETITORS_PER_PROJECT,
                }),
              }}
            />
            {isCheckoutModalOpen && (
              <TurnstileProvider
                WrappedComponent={checkoutModal}
                isOpen={isCheckoutModalOpen}
                onClose={this.handleCloseCheckoutModal}
                title={titleUpgradePlan}
                reachedText={reachedText}
                isAddProjectFlow
                shouldOpenATPModal={false}
                onCheckoutSuccess={() =>
                  this.setState({
                    titleUpgradePlan: '',
                    reachedText: '',
                    isCheckoutModalOpen: false,
                  })
                }
                maxLimitReachedText={this.getMaxLimitReachedText({
                  MAX_KEYWORDS_PER_PROJECT,
                  MAX_COMPETITORS_PER_PROJECT,
                })}
              />
            )}
          </div>
        </ModalContent>
      </Modal>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  user: selectCurrentUser(),
  cachedLocations: selectCachedLocations(),
  newLimitsChangingPlans: selectNewLimitsChangingPlans(),
})

const mapDispatchToProps = (dispatch) => ({
  addProject: (payload, leadsData, onComplete) =>
    dispatch(addProject(payload, leadsData, onComplete)),
})

export default connect(mapStateToProps, mapDispatchToProps)(AddProjectModal)
